<!-- FAB Trigger Button -->
<button
  class="ai-chat-fab"
  [class.active]="isOpen"
  (click)="panelService.toggle()"
  matTooltip="AI Assistant">
  <mat-icon *ngIf="!isOpen">smart_toy</mat-icon>
  <mat-icon *ngIf="isOpen">close</mat-icon>
</button>

<!-- Chat Popup -->
<div
  *ngIf="isOpen"
  class="ai-chat-popup"
  [@chatPopup]>

  <div class="ai-chat-container" [ngClass]="config.containerClass || ''">

    <!-- ======================== HEADER ======================== -->
    <div class="chat-header" *ngIf="showHeader">
      <div class="chat-header-left">
        <div class="header-icon-wrapper">
          <mat-icon>{{ icon }}</mat-icon>
        </div>
        <div class="header-info">
          <h4>{{ title }}</h4>
          <span class="session-status" [class.active]="sessionActive">
            <span class="status-dot"></span>
            {{ sessionActive ? 'Connected' : 'Connecting...' }}
          </span>
        </div>
      </div>
      <div class="chat-header-actions">
        <button mat-icon-button [matMenuTriggerFor]="chatMenu" matTooltip="Options">
          <mat-icon>more_vert</mat-icon>
        </button>
        <mat-menu #chatMenu="matMenu">
          <button mat-menu-item (click)="newSession()">
            <mat-icon>refresh</mat-icon>
            <span>New conversation</span>
          </button>
          <button mat-menu-item (click)="exportChat()" [disabled]="messages.length === 0">
            <mat-icon>download</mat-icon>
            <span>Export chat</span>
          </button>
        </mat-menu>
        <button mat-icon-button (click)="panelService.close()" matTooltip="Close">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <!-- ======================== MESSAGES ======================== -->
    <div class="chat-messages" #chatContainer>

      <!-- Initializing State -->
      <div *ngIf="isInitializing" class="state-container">
        <mat-spinner diameter="28"></mat-spinner>
        <span class="state-text">Starting AI session...</span>
      </div>

      <!-- Empty State with Suggested Prompts -->
      <div *ngIf="!isInitializing && messages.length === 0" class="empty-state">
        <div class="empty-icon-wrapper">
          <mat-icon>{{ icon }}</mat-icon>
        </div>
        <h4 class="empty-title">{{ title }}</h4>
        <p class="empty-subtitle">Ask questions, analyze documents, or get help with your tasks.</p>

        <div class="suggested-prompts" *ngIf="suggestedPrompts.length > 0">
          <span class="prompts-label">Try asking</span>
          <div class="prompts-grid">
            <button
              *ngFor="let prompt of suggestedPrompts"
              class="prompt-chip"
              (click)="useSuggestedPrompt(prompt)">
              <mat-icon>arrow_forward</mat-icon>
              {{ prompt }}
            </button>
          </div>
        </div>
      </div>

      <!-- Messages -->
      <ng-container *ngIf="!isInitializing && messages.length > 0">
        <div
          *ngFor="let msg of messages; trackBy: trackByMessageId"
          class="chat-message"
          [class.own-message]="msg.role === 'user'"
          [class.assistant-message]="msg.role === 'assistant'"
          [class.error-message]="msg.isError">

          <!-- User Message -->
          <ng-container *ngIf="msg.role === 'user'">
            <div class="message-row own">
              <div class="message-content user-content">
                <div class="message-meta align-right">
                  <span class="message-time">{{ formatTime(msg.timestamp) }}</span>
                  <span class="sender-name own-name">{{ getUserName() }}</span>
                </div>
                <div class="message-bubble own-bubble">
                  <div class="message-text">{{ msg.content }}</div>
                  <!-- Attachment Badges -->
                  <div *ngIf="msg.attachments?.length" class="message-attachments">
                    <div *ngFor="let att of msg.attachments" class="attachment-badge">
                      <mat-icon>{{ getFileIcon(att.name) }}</mat-icon>
                      <span class="attachment-name">{{ att.name }}</span>
                      <span class="attachment-size" *ngIf="att.size">{{ formatFileSize(att.size) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="message-avatar">
                <span class="avatar-text user-avatar">{{ getUserInitials() }}</span>
              </div>
            </div>
          </ng-container>

          <!-- Assistant Message -->
          <ng-container *ngIf="msg.role === 'assistant'">
            <div class="message-row">
              <div class="message-avatar">
                <span class="avatar-text ai-avatar">
                  <mat-icon>smart_toy</mat-icon>
                </span>
              </div>
              <div class="message-content ai-content">
                <div class="message-meta">
                  <span class="sender-name ai-name">AI Assistant</span>
                  <span class="message-time">{{ formatTime(msg.timestamp) }}</span>
                </div>
                <div class="message-bubble ai-bubble" [class.error-bubble]="msg.isError">
                  <!-- Streaming indicator -->
                  <div *ngIf="msg.isStreaming && !msg.content" class="typing-indicator">
                    <span class="dot"></span>
                    <span class="dot"></span>
                    <span class="dot"></span>
                  </div>
                  <!-- Rendered markdown content -->
                  <div
                    *ngIf="msg.renderedContent"
                    class="message-text markdown-content"
                    [innerHTML]="msg.renderedContent">
                  </div>
                  <!-- Plain text fallback -->
                  <div
                    *ngIf="!msg.renderedContent && msg.content"
                    class="message-text">
                    {{ msg.content }}
                  </div>
                  <!-- Streaming cursor -->
                  <span *ngIf="msg.isStreaming && msg.content" class="streaming-cursor"></span>
                </div>
                <!-- Message Actions -->
                <div class="message-actions" *ngIf="!msg.isStreaming">
                  <button
                    class="action-btn"
                    matTooltip="Copy"
                    (click)="copyMessage(msg)">
                    <mat-icon>content_copy</mat-icon>
                  </button>
                  <button
                    *ngIf="msg.isError"
                    class="action-btn retry-btn"
                    matTooltip="Retry"
                    (click)="retryLastMessage()">
                    <mat-icon>replay</mat-icon>
                  </button>
                </div>
              </div>
            </div>
          </ng-container>
        </div>
      </ng-container>
    </div>

    <!-- ======================== PENDING FILES ======================== -->
    <div class="pending-files-bar" *ngIf="pendingFiles.length > 0">
      <div *ngFor="let file of pendingFiles; let i = index" class="pending-file">
        <mat-icon>{{ getFileIcon(file.name) }}</mat-icon>
        <span class="pending-file-name">{{ file.name }}</span>
        <span class="pending-file-size">{{ formatFileSize(file.size) }}</span>
        <button class="pending-file-remove" (click)="removePendingFile(i)" matTooltip="Remove">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <!-- ======================== INPUT ======================== -->
    <div class="chat-input-area">
      <div class="input-wrapper" [class.disabled]="!sessionActive || isInitializing">
        <!-- Attachment Button -->
        <button
          *ngIf="enableDocs"
          class="input-action-btn attach-btn"
          (click)="triggerFileUpload()"
          [disabled]="isStreaming || isLoading"
          matTooltip="Attach document">
          <mat-icon>attach_file</mat-icon>
        </button>

        <!-- Text Input -->
        <textarea
          class="chat-input"
          [formControl]="messageControl"
          [placeholder]="placeholder"
          (keydown)="onKeyDown($event)"
          rows="1"
          [attr.disabled]="(!sessionActive || isInitializing) ? '' : null">
        </textarea>

        <!-- Send Button -->
        <button
          class="input-action-btn send-btn"
          [class.active]="messageControl.value?.trim() || pendingFiles.length > 0"
          (click)="sendMessage()"
          [disabled]="(!messageControl.value?.trim() && !pendingFiles.length) || isStreaming || isLoading || !sessionActive"
          matTooltip="Send message (Enter)">
          <mat-icon *ngIf="!isStreaming && !isLoading">send</mat-icon>
          <mat-spinner *ngIf="isStreaming || isLoading" diameter="18"></mat-spinner>
        </button>
      </div>

      <span class="input-hint">
        Press <strong>Enter</strong> to send, <strong>Shift+Enter</strong> for new line
      </span>

      <!-- Hidden File Input -->
      <input
        #fileInput
        type="file"
        hidden
        accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.doc,.docx,.xls,.xlsx"
        (change)="onFileSelected($event)"
        multiple />
    </div>
  </div>
</div>
